import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';

// "Needs attention" — GET /admin/attention: every queue waiting on this admin
// (approvals, payouts, refunds, tickets…), already filtered to their scopes.
// One cached query feeds both the dashboard panel and the sidebar badges.

export interface AttentionItem {
  key: string;
  label: string;
  hint: string;
  count: number;
  href: string;
  nav: string;
  tone: 'warning' | 'danger' | 'info';
  links?: { label: string; href: string }[];
}

export interface Attention {
  total: number;
  items: AttentionItem[];
  byNav: Record<string, number>;
  generatedAt: string;
}

export const ATTENTION_KEY = ['admin', 'attention'] as const;

export function useAttention() {
  return useQuery({
    queryKey: ATTENTION_KEY,
    queryFn: async () => (await api.get<Attention>('/admin/attention')).data,
    // New approvals arrive without any action here, so poll; a minute is
    // fresh enough for a back-office queue and costs a handful of COUNTs.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
  });
}

/**
 * A list page's status tab, kept in the URL as `?tab=` so a dashboard or
 * sidebar link can open the page on the right queue, and back/refresh keep it.
 * Unknown values fall back to [fallback].
 */
export function useUrlTab<T extends string>(allowed: readonly T[], fallback: T): [T, (next: T) => void] {
  const [params, setParams] = useSearchParams();
  const raw = params.get('tab');
  const tab = (raw && (allowed as readonly string[]).includes(raw) ? raw : fallback) as T;
  const setTab = useCallback(
    (next: T) =>
      setParams(
        (cur) => {
          const p = new URLSearchParams(cur);
          if (next === fallback) p.delete('tab');
          else p.set('tab', next);
          return p;
        },
        { replace: true },
      ),
    [setParams, fallback],
  );
  return [tab, setTab];
}
